import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import PropertyCard from '@/components/PropertyCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { Search, MapPin, Building2, TrendingUp, ShieldCheck, Users, Sparkles, Award } from 'lucide-react';

const HERO_IMG = 'https://images.unsplash.com/photo-1649989184479-73ab3b750a73?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsdXh1cnklMjBpbmRpYW4lMjBhcGFydG1lbnQlMjBleHRlcmlvciUyMGJ1aWxkaW5nJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHxibHVlfDE3ODk2Mzg3NTV8MA&ixlib=rb-4.1.0&q=85';

const Home = () => {
  const navigate = useNavigate();
  const [searchType, setSearchType] = useState('buy');
  const [searchCity, setSearchCity] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [popularCities, setPopularCities] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [featuredBrokers, setFeaturedBrokers] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [featured, popular, cities, brokers] = await Promise.all([
        api.get('/public/featured-projects?limit=6'),
        api.get('/public/popular-cities'),
        api.get('/public/cities'),
        api.get('/brokers?limit=6')
      ]);
      setFeaturedProjects(featured.data);
      setPopularCities(popular.data);
      setAllCities(cities.data);
      setFeaturedBrokers(brokers.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchCity) params.append('city', searchCity);
    if (searchQuery) params.append('q', searchQuery);
    if (searchType === 'commercial') params.append('category', 'commercial');
    if (searchType === 'new_launch') params.append('project_status', 'new_launch');
    navigate(`/search?${params.toString()}`);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar transparent />

      {/* HERO */}
      <section className="relative">
        <div className="relative h-[550px] md:h-[600px] overflow-hidden">
          <img src={HERO_IMG} alt="hero" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F2C59]/70 via-[#0F2C59]/50 to-[#0F2C59]/85" />

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-center">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/15 backdrop-blur-sm rounded-full text-white text-xs font-medium border border-white/25 mb-5">
                <Sparkles size={14} className="text-[#F97316]" />
                <span>India's Premium Real Estate Marketplace</span>
              </div>

              <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-tight">
                Find Your <span className="text-[#F97316]">Dream Home</span><br />
                Across Pan-India
              </h1>
              <p className="mt-4 text-lg text-slate-200 max-w-2xl">
                Discover verified projects, connect with trusted RERA-registered brokers, and make informed property decisions.
              </p>
            </div>

            {/* Search Card */}
            <div className="mt-8 max-w-4xl bg-white rounded-lg hero-search-shadow p-4 sm:p-6">
              <Tabs value={searchType} onValueChange={setSearchType} className="mb-4">
                <TabsList className="bg-slate-100">
                  <TabsTrigger value="buy" data-testid="search-tab-buy">Buy</TabsTrigger>
                  <TabsTrigger value="rent" data-testid="search-tab-rent">Rent</TabsTrigger>
                  <TabsTrigger value="commercial" data-testid="search-tab-commercial">Commercial</TabsTrigger>
                  <TabsTrigger value="new_launch" data-testid="search-tab-new-launch">New Launch</TabsTrigger>
                </TabsList>
              </Tabs>

              <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                <div className="md:col-span-4">
                  <Select value={searchCity} onValueChange={setSearchCity}>
                    <SelectTrigger className="h-12 border-slate-200" data-testid="search-city-select">
                      <div className="flex items-center gap-2">
                        <MapPin size={16} className="text-slate-400" />
                        <SelectValue placeholder="Select City" />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      {allCities.map(c => (
                        <SelectItem key={c.name} value={c.name}>{c.name}, {c.state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-6">
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search projects, localities, builders..."
                      className="h-12 pl-10 border-slate-200"
                      data-testid="search-query-input"
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <Button onClick={handleSearch} className="w-full h-12 bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold" data-testid="search-submit-btn">
                    Search
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: Building2, label: 'Verified Projects', value: '10K+' },
            { icon: Users, label: 'Trusted Brokers', value: '5K+' },
            { icon: MapPin, label: 'Cities Covered', value: '50+' },
            { icon: ShieldCheck, label: 'RERA Verified', value: '100%' }
          ].map((stat, i) => (
            <div key={i} className="bg-white rounded-lg border border-slate-200 p-5">
              <stat.icon size={22} className="text-[#F97316] mb-2" />
              <div className="font-display font-extrabold text-2xl text-[#0F2C59]">{stat.value}</div>
              <div className="text-xs text-slate-500 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED PROJECTS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-[#F97316]">Handpicked</div>
            <h2 className="font-display font-bold text-2xl sm:text-3xl text-[#0F2C59] mt-1">Featured Projects</h2>
          </div>
          <Link to="/search" className="text-sm font-semibold text-[#0F2C59] hover:text-[#F97316]" data-testid="view-all-featured">View All →</Link>
        </div>

        {featuredProjects.length === 0 ? (
          <div className="bg-white rounded-lg border border-dashed border-slate-300 p-12 text-center">
            <Building2 size={40} className="mx-auto text-slate-300 mb-3" />
            <div className="text-slate-500">No projects yet. Be the first broker to list!</div>
            <Link to="/become-broker">
              <Button className="mt-4 bg-[#F97316] hover:bg-[#EA580C]" data-testid="empty-become-broker">Become a Broker</Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {featuredProjects.map(project => (
              <PropertyCard key={project.id} project={project} />
            ))}
          </div>
        )}
      </section>

      {/* POPULAR CITIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#F97316]">Explore</div>
          <h2 className="font-display font-bold text-2xl sm:text-3xl text-[#0F2C59] mt-1">Popular Cities</h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {allCities.slice(0, 12).map((city, i) => (
            <Link
              key={city.name}
              to={`/search?city=${city.name}`}
              className="group bg-white rounded-lg border border-slate-200 p-4 hover:border-[#F97316] hover:shadow-md text-center"
              data-testid={`city-card-${city.name}`}
            >
              <MapPin size={18} className="mx-auto text-[#0F2C59] group-hover:text-[#F97316] mb-2" />
              <div className="font-semibold text-sm text-slate-900">{city.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">{city.state}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED BROKERS */}
      {featuredBrokers.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-[#F97316]">Trusted Network</div>
              <h2 className="font-display font-bold text-2xl sm:text-3xl text-[#0F2C59] mt-1">Verified Brokers</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {featuredBrokers.map(broker => (
              <Link
                key={broker.id}
                to={`/broker/${broker.slug}`}
                className="bg-white rounded-lg border border-slate-200 p-5 hover:border-[#F97316] hover:shadow-md flex items-center gap-4 group"
                data-testid={`broker-card-${broker.slug}`}
              >
                <div className="w-14 h-14 rounded-full bg-[#0F2C59] text-white flex items-center justify-center font-black text-xl flex-shrink-0 group-hover:bg-[#F97316] transition-colors">
                  {broker.full_name?.charAt(0)?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <div className="font-bold text-slate-900 truncate">{broker.full_name}</div>
                    <ShieldCheck size={14} className="text-emerald-600 flex-shrink-0"/>
                  </div>
                  {broker.agency_name && <div className="text-xs text-slate-500 truncate">{broker.agency_name}</div>}
                  <div className="mt-1 flex items-center gap-3 text-xs text-slate-500">
                    <span className="flex items-center gap-0.5"><MapPin size={11}/> {broker.primary_city}</span>
                    <span className="flex items-center gap-0.5"><Award size={11}/> {broker.years_of_experience}+ yrs</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* WHY PBK */}
      <section className="bg-white border-y border-slate-200 py-16 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#F97316]">Why PropertyByKilo</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0F2C59] mt-2">India's Most Trusted PropTech Platform</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: ShieldCheck, title: 'Verified Brokers', desc: 'Every broker is RERA-verified and KYC-approved by our admin team.' },
              { icon: TrendingUp, title: 'Real Insights', desc: 'Live market data, price trends, and analytics for informed decisions.' },
              { icon: Award, title: 'Zero Brokerage Options', desc: 'Direct builder listings with transparent pricing and offers.' }
            ].map((item, i) => (
              <div key={i} className="text-center p-6 rounded-lg border border-slate-200 bg-slate-50/50">
                <div className="w-12 h-12 rounded-full bg-[#F97316]/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon size={24} className="text-[#F97316]" />
                </div>
                <h3 className="font-bold text-lg text-slate-900">{item.title}</h3>
                <p className="text-sm text-slate-600 mt-2">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="rounded-2xl bg-gradient-to-br from-[#0F2C59] to-[#1E3A6F] p-8 md:p-12 text-white overflow-hidden relative">
          <div className="max-w-2xl relative z-10">
            <h2 className="font-display font-bold text-3xl sm:text-4xl">Are You a Real Estate Broker?</h2>
            <p className="mt-3 text-slate-200">Join thousands of brokers who trust PropertyByKilo to power their business. Get your own dashboard, landing pages, and lead management CRM.</p>
            <Link to="/become-broker">
              <Button size="lg" className="mt-6 bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold" data-testid="cta-become-broker">Become a Broker Today</Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
